import { useCallback, useEffect, useState } from "react";
import ContentAsync from "../../parts/ContentAsync";
import Api, { FinanceReceiptsClientResponse } from "../../Api";
import { useParams } from "react-router-dom";
import ClientBar from "../../parts/ClientBar";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import EntityList from "../../parts/EntityList";
import Fab from "../../parts/Fab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { intToBrl } from "../../utils";
import classNames from "classnames";
import { Client } from "../../types";

const FinanceReceiptsClient = () => {
  const { uuid } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [data, setData] = useState<
    FinanceReceiptsClientResponse["receipts"] | null
  >(null);

  const load = useCallback(() => {
    Api.financeReceiptsClient(uuid!).then((data) => [
      setClient(data.client),
      setData(data.receipts),
    ]);
  }, [uuid]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ContentAsync loading={!data}>
      {client && (
        <div>
          <ClientBar
            action="Recibos de"
            client={client.name}
            url={`/financeiro/recibos/clientes`}
          />

          <Section>
            {data!.length === 0 && <DiscreetMessage text="Nenhum recibo" />}
          </Section>

          {data!.length > 0 && (
            <EntityList
              entities={data}
              Card={ReceiptCard}
              props={{ defaultEmail: client.email, reload: load }}
            />
          )}

          <Fab href={`/financeiro/recibos/criar/${client.uuid}`} />
        </div>
      )}
    </ContentAsync>
  );
};

export default FinanceReceiptsClient;

const ReceiptCard = ({
  entity: { receipt, userName },
  expanded,
  setExpanded,
  props: { defaultEmail, reload },
}: {
  entity: FinanceReceiptsClientResponse["receipts"][number];
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  props: { defaultEmail: string; reload: () => void };
}) => {
  const [mode, setMode] = useState("buttons");
  const [email, setEmail] = useState(defaultEmail);
  const [action, setAction] = useState(false);

  const toggle = () => {
    setMode("buttons");
    setEmail(defaultEmail);
    setExpanded(!expanded);
  };

  const cancel = () => {
    if (window.confirm("Confirmar o cancelamento do recibo?")) {
      setAction(true);
      Api.financeCancelGeneric(receipt.uuid).then(reload);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (window.confirm(`Confirmar envio do recibo por email para ${email}?`)) {
      setAction(true);
      Api.financeEmailGeneric(receipt.uuid, email).then(reload);
    }
  };

  return (
    <div className="border rounded-lg shadow-sm text-sm">
      <div
        className={classNames("rounded-lg", {
          "hover:bg-slate-50 cursor-pointer": !expanded && !receipt.canceled,
        })}
        onClick={toggle}
      >
        <div className="text-xs px-4 py-1 border-b">
          <FontAwesomeIcon icon="calendar" className="w-3 mr-3 text-gray-400" />
          <span className="text-gray-500 font-light">
            {moment.utc(receipt.date).format("DD/MM/YYYY à\\s hh:mm")}
          </span>
        </div>

        {!receipt.canceled && (
          <div className="px-4 py-2.5 border-b">
            <FontAwesomeIcon
              icon="sack-dollar"
              className="w-3 mr-3 text-gray-400"
            />
            <span className="text-gray-700 font-semibold">
              {intToBrl(receipt.amount)}
            </span>
          </div>
        )}

        {receipt.canceled && (
          <div className="px-4 py-2.5 border-b bg-red-50">
            <FontAwesomeIcon
              icon="sack-dollar"
              className="w-3 mr-3 text-red-300"
            />
            <span className="text-red-400 line-through">
              {intToBrl(receipt.amount)}
            </span>
            <span className="ml-3 font-bold text-red-700">Cancelado</span>
          </div>
        )}

        <div
          className={classNames("bg-gray-50 text-gray-600 py-2 px-4", {
            "rounded-b-lg truncate": !expanded,
          })}
        >
          {receipt.reference}
        </div>
      </div>

      {expanded && (
        <div className="text-xs px-4 py-1 border-t">
          <FontAwesomeIcon icon="user" className="w-3 mr-3 text-gray-400" />
          <span className="text-gray-500 font-light">{userName}</span>
        </div>
      )}

      {expanded && receipt.sends.length > 0 && (
        <div>
          {receipt.sends.map((send) => (
            <div className="text-xs px-4 py-1 border-t overflow-scroll no-scrollbar whitespace-nowrap">
              <FontAwesomeIcon
                icon="envelope"
                className="w-3 mr-3 text-gray-400"
              />
              <span className="text-gray-500 font-light">
                <span className="tracking-tighter mr-2 font-mono">
                  {moment.utc(send.date).format("DD/MM/YY")}
                </span>
                {send.to}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs">
        {expanded && mode === "buttons" && (
          <div className="border-t flex">
            <button
              className="p-3 border-r rounded-bl-lg font-bold hover:bg-slate-50"
              onClick={() =>
                window.open(`/externo/recibo/${receipt.externalHash}`)
              }
              disabled={action}
            >
              Abrir
            </button>

            {!receipt.canceled && (
              <button
                className="p-3 border-r font-bold hover:bg-slate-50"
                onClick={() => setMode("send")}
                disabled={action}
              >
                Enviar
              </button>
            )}

            {!receipt.canceled && (
              <button
                className="p-3 border-r font-bold hover:bg-red-50 text-red-700"
                onClick={cancel}
                disabled={action}
              >
                Cancelar
              </button>
            )}

            <button
              className="rounded-br-lg p-3 flex-1 text-right hover:bg-slate-50"
              onClick={toggle}
            >
              <FontAwesomeIcon icon="chevron-up" />
            </button>
          </div>
        )}

        {expanded && mode === "send" && (
          <form className="border-t flex" onSubmit={submit}>
            <input
              type="email"
              className="flex-1 border rounded px-2 m-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />

            <button
              type="button"
              className="py-3 px-2 border-l font-bold hover:bg-slate-50"
              onClick={() => [setMode("buttons"), setEmail(defaultEmail)]}
              disabled={action}
            >
              Cancelar
            </button>

            <button
              className="py-3 px-2 rounded-br-lg bg-sky-600 text-white font-extrabold hover:bg-sky-500"
              disabled={action}
            >
              Enviar
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
